import React, { useState, ReactNode, useEffect } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

// components
import FloorPlanMap from "./components/FloorPlanMap";
import FloorPlanControls from "./components/FloorPlanControls";
import Pagination from "@/components/Pagination";
import RestrictedView from "@/components/RestrictedView";
import Select from "@/shared/Select";

// helpers
import separateSpaceBySpaceAssetType from "@/globals/separateSpaceBySpaceAssetType";

// interface
import { ILocationSpaceItem } from "@/types/api/Locations";

// assets
import styles from "@/assets/styles/pages/location-single-page/location-single-page-components.module.scss";

export interface FloorPlanItem {
  id: number;
  value: string;
  label: string;
  image?: {
    url: string;
  };
}

interface FloorPlanProps {
  items: FloorPlanItem[];
  spaces: ILocationSpaceItem[];
  activeSpaceId?: number;
  rest?: ReactNode;
  isRestricted?: boolean;
  restrictedTitle?: string | React.ReactNode | JSX.Element;
  restrictedSubtitle?: string | React.ReactNode | JSX.Element;
  restrictedButtonText?: string;
  activationSpacesCount?: number;
  mediaOpportunitySpacesCount?: number;
  onRestrictedButtonClick?: () => void;
  onPinClick?: () => void;
}

const SPACE_TYPE_OPTIONS = [
  { label: "Activation spaces", value: 1 },
  { label: "Media opportunities", value: 2 },
];

const LocationFloorPlan = (props: FloorPlanProps) => {
  const {
    items,
    spaces,
    onPinClick,
    isRestricted,
    restrictedTitle,
    restrictedSubtitle,
    restrictedButtonText,
    activationSpacesCount,
    mediaOpportunitySpacesCount,
    onRestrictedButtonClick,
    ...rest
  } = props;

  const [selectedFloor, setSelectedFloor] = useState(items ? items[0] : null);
  const [selectedFloorIndex, setSelectedFloorIndex] = useState(0);
  const [selectSpaceType, setSelectSpaceType] = useState(SPACE_TYPE_OPTIONS[0].value);

  const spacesToShow = (spaces || []).filter(item => item?.floor === selectedFloor?.id);

  const handleChangeFloorPlan = (index: number) => {
    setSelectedFloorIndex(index - 1);
    setSelectedFloor(items[index - 1]);
  }

  const {
    activationSpaces,
    mediaOpportunitySpaces
  } = separateSpaceBySpaceAssetType(spacesToShow);

  const spaceToRender = selectSpaceType === 1 ? activationSpaces : mediaOpportunitySpaces;

  const activeFloorPlan = SPACE_TYPE_OPTIONS.find(option => option.value === selectSpaceType);

  const isShowSpaceTypeFilter = !!activationSpacesCount && !!mediaOpportunitySpacesCount;

  // effects
  useEffect(() => {
    if (!activationSpacesCount || !mediaOpportunitySpacesCount) {
      setSelectSpaceType(activationSpacesCount ? 1 : 2);
    }
  }, [activationSpacesCount, mediaOpportunitySpacesCount]);

  // render
  return (
    <section className={styles["locl-location-floor-plan"]} { ...rest }>
      <div className={styles["locl-location-floor-plan__head"]}>
        <div className={styles["locl-location-floor-plan__title"]}>
          Floor plan
        </div>
        {
          isShowSpaceTypeFilter && (
            <div className={styles["locl-location-floor-plan__space-type"]}>
              <Select
                options={SPACE_TYPE_OPTIONS}
                onChange={e => setSelectSpaceType(e.value as number)}
                value={activeFloorPlan}
                fullWidth
                aria-label="locl-location-floor-plan-space-type-select"
                menuListTestId="locl-location-floor-plan-space-type-select-list"
              />
            </div>
          )
        }
      </div>
      <div className={styles["locl-location-floor-plan__body"]}>
        <div
          className={styles["locl-location-floor-plan__name"]}
          data-testid="locl-location-floor-plan-name"
        >
          {selectedFloor?.label}
        </div>
        <div className={styles["locl-location-floor-plan__content"]}>
          <TransformWrapper
            initialScale={1}
            minScale={1}
            maxScale={5}
            wheel={{ wheelDisabled: true, touchPadDisabled: true }}
          >
            {
              (actions) => (
                <React.Fragment>
                  <TransformComponent>
                    <FloorPlanMap
                      spaces={spaceToRender}
                      floorPlanImage={(selectedFloor?.value || selectedFloor?.image?.url) as string}
                      onPinClick={onPinClick}
                    />
                  </TransformComponent>
                  <FloorPlanControls
                    zoomIn={actions.zoomIn}
                    zoomOut={actions.zoomOut}
                  />
                </React.Fragment>
              )
            }
          </TransformWrapper>
        </div>
        {
          items.length > 1 && (
            <div className={styles["locl-location-floor-plan__pagination"]}>
              <Pagination
                total={items.length}
                current={selectedFloorIndex + 1}
                pageSize={1}
                onChange={handleChangeFloorPlan}
              />
            </div>
          )
        }
        {
          isRestricted && (
            <RestrictedView
              title={restrictedTitle}
              subtitle={restrictedSubtitle}
              buttonText={restrictedButtonText || ""}
              onButtonClick={() => onRestrictedButtonClick && onRestrictedButtonClick()}
            />
          )
        }
      </div>
    </section>
  );
}

export default LocationFloorPlan;
